// AI Document Management Service
// Handles document upload, text extraction, chunking, and embedding generation

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { AIDocument, DocumentType, DocumentChunk, ChunkMetadata } from '@/types/ai-admin';
import { doclingClient } from '@/services/doclingClient';
import { queueAIDocumentForProcessing } from '@/services/loanDocumentService';

// Re-export for convenience
export { queueAIDocumentForProcessing };
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const log = createComponentLogger('AIDocumentService', 'ai-documents');

// ============================================================================
// Configuration
// ============================================================================

const CHUNK_SIZE = 512; // tokens
const CHUNK_OVERLAP = 0.2; // 20% overlap
const STORAGE_BUCKET = 'ai-documents';
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

// Approximate tokens (1 token ≈ 4 characters for English text)
const CHARS_PER_TOKEN = 4;

// Cohere API batch limit
const EMBEDDING_BATCH_SIZE = 96;

// Configure PDF.js worker - use Vite asset import
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// ============================================================================
// Text Extraction
// ============================================================================

/**
 * Extract text content from uploaded file
 */
async function extractText(file: File): Promise<string> {
  const fileType = file.type;

  try {
    if (fileType === 'text/plain' || fileType === 'text/markdown') {
      return await file.text();
    }

    if (fileType === 'application/pdf') {
      log.info('Extracting text from PDF using Docling', { fileName: file.name, size: file.size });

      try {
        // First, try Docling for high-quality extraction (90%+ accuracy)
        // Enable page breaks for citation linking (format: --- Page N ---)
        const result = await doclingClient.extractPDF(file, {
          outputFormat: 'markdown',
          includeTables: true,
          includeImages: false,
          includePageBreaks: true
        });

        if (result.status === 'error') {
          throw new Error(result.error || 'Docling extraction failed');
        }

        if (!result.content || result.content.trim().length === 0) {
          throw new Error('Docling returned empty content');
        }

        log.info('PDF text extracted successfully via Docling', {
          pages: result.metadata.pages,
          contentLength: result.content.length,
          processingTime: result.metadata.processing_time_seconds,
          speed: result.metadata.pages_per_second,
          gpu: result.metadata.gpu_used
        });

        return result.content;
      } catch (doclingError) {
        // Fallback to PDF.js if Docling fails (service down, network error, etc.)
        log.warn('Docling extraction failed, falling back to PDF.js', {}, doclingError as Error);

        // Convert File to ArrayBuffer for PDF.js
        const arrayBuffer = await file.arrayBuffer();

        // Load PDF document
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        log.info('PDF loaded with PDF.js fallback', { numPages: pdf.numPages });

        // Extract text from all pages with page break markers for citation linking
        const textParts: string[] = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map((item) => ('str' in item ? item.str : ''))
            .join(' ');
          // Add page break marker for citation linking (same format as Docling)
          textParts.push(`--- Page ${pageNum} ---\n${pageText}`);
        }

        const fullText = textParts.join('\n\n');

        if (!fullText || fullText.trim().length === 0) {
          throw new Error('PDF appears to be empty or contains only images');
        }

        log.info('PDF text extracted via PDF.js fallback', {
          pages: pdf.numPages,
          textLength: fullText.length
        });

        return fullText;
      }
    }

    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      // DOCX parsing would require mammoth.js
      throw new Error('DOCX parsing not yet implemented. Please convert to PDF or text first.');
    }

    // Fallback: try to read as text
    return await file.text();
  } catch (error) {
    log.error('Text extraction failed', { fileType, fileName: file.name }, error as Error);
    throw new Error(`Failed to extract text from ${file.name}: ${(error as Error).message}`);
  }
}

// ============================================================================
// Chunking Logic
// ============================================================================

// Page break marker pattern (format from Docling: "--- Page N ---")
const PAGE_BREAK_PATTERN = /---\s*Page\s+(\d+)\s*---/gi;

interface PageBoundary {
  pageNumber: number;
  startPosition: number;
}

/**
 * Parse page break markers from text and build a map of positions to page numbers.
 * Returns boundaries adjusted for CLEANED text positions (after marker removal).
 */
function buildPageMap(text: string): PageBoundary[] {
  const boundaries: PageBoundary[] = [];
  let match: RegExpExecArray | null;
  let cumulativeOffset = 0; // Track chars removed by previous markers

  // Reset regex state
  PAGE_BREAK_PATTERN.lastIndex = 0;

  while ((match = PAGE_BREAK_PATTERN.exec(text)) !== null) {
    // Position in cleaned text = original position - chars removed so far
    const cleanedPosition = match.index - cumulativeOffset;

    boundaries.push({
      pageNumber: parseInt(match[1], 10),
      startPosition: cleanedPosition
    });

    // Add this marker's length to cumulative offset
    cumulativeOffset += match[0].length;
  }

  // If no page markers found, assume entire document is page 1
  if (boundaries.length === 0) {
    boundaries.push({ pageNumber: 1, startPosition: 0 });
  }

  return boundaries;
}

/**
 * Get the page number at a specific character position
 */
function getPageAtPosition(position: number, boundaries: PageBoundary[]): number {
  // Find the last boundary that starts before or at this position
  for (let i = boundaries.length - 1; i >= 0; i--) {
    if (boundaries[i].startPosition <= position) {
      return boundaries[i].pageNumber;
    }
  }
  return boundaries[0]?.pageNumber || 1;
}

/**
 * Split text into overlapping chunks for embedding
 * Uses character-based chunking with overlap to preserve context
 * Tracks page numbers for each chunk for citation linking
 */
function chunkText(text: string): DocumentChunk[] {
  const chunks: DocumentChunk[] = [];
  const chunkSizeChars = CHUNK_SIZE * CHARS_PER_TOKEN;
  const overlapChars = Math.floor(chunkSizeChars * CHUNK_OVERLAP);
  const stepSize = chunkSizeChars - overlapChars;

  // Step 1: Normalize line endings, collapse newlines, and trim FIRST
  // This ensures page marker positions align with final text positions
  const normalizedText = text
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\n{3,}/g, '\n\n') // Collapse excessive newlines
    .trim(); // Trim before mapping to avoid position drift

  // Step 2: Build page boundary map from normalized text (accounts for marker removal)
  const pageBoundaries = buildPageMap(normalizedText);

  // Step 3: Remove page break markers to get final chunking text
  const cleanText = normalizedText
    .replace(PAGE_BREAK_PATTERN, ''); // Remove page break markers

  if (cleanText.length === 0) {
    throw new Error('Document contains no text content');
  }

  let position = 0;
  let index = 0;

  while (position < cleanText.length) {
    const end = Math.min(position + chunkSizeChars, cleanText.length);
    let chunkContent = cleanText.slice(position, end);

    // Try to break at sentence boundaries for better context
    if (end < cleanText.length) {
      const lastPeriod = chunkContent.lastIndexOf('. ');
      const lastNewline = chunkContent.lastIndexOf('\n');
      const breakPoint = Math.max(lastPeriod, lastNewline);

      if (breakPoint > chunkSizeChars * 0.5) {
        chunkContent = chunkContent.slice(0, breakPoint + 1).trim();
      }
    }

    const tokenCount = Math.ceil(chunkContent.length / CHARS_PER_TOKEN);

    // Calculate page range for this chunk
    const chunkStartPos = position;
    const chunkEndPos = position + chunkContent.length;
    const pageStart = getPageAtPosition(chunkStartPos, pageBoundaries);
    const pageEnd = getPageAtPosition(chunkEndPos, pageBoundaries);

    // Build metadata for this chunk
    const metadata: ChunkMetadata = {
      page_start: pageStart,
      page_end: pageEnd
    };

    chunks.push({
      index,
      content: chunkContent.trim(),
      tokenCount,
      metadata
    });

    position += stepSize;
    index++;
  }

  log.info('Text chunked successfully with page tracking', {
    totalChars: cleanText.length,
    chunkCount: chunks.length,
    avgChunkSize: Math.round(chunks.reduce((sum, c) => sum + c.tokenCount, 0) / chunks.length),
    pageRange: `${pageBoundaries[0]?.pageNumber || 1}-${pageBoundaries[pageBoundaries.length - 1]?.pageNumber || 1}`,
    totalPages: pageBoundaries.length
  });

  return chunks;
}

// ============================================================================
// Embedding Generation
// ============================================================================

/**
 * Generate embeddings for document chunks using Cohere via edge function
 * Implements batch processing to handle documents with >96 chunks
 */
async function generateEmbeddings(chunks: DocumentChunk[]): Promise<DocumentChunk[]> {
  const MAX_RETRIES = 3;
  const INITIAL_RETRY_DELAY = 1000; // 1 second

  try {
    const texts = chunks.map(c => c.content);
    const totalChars = texts.reduce((sum, t) => sum + t.length, 0);

    // Get current session for authentication
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('User session not found');
    }

    // Split into batches if needed
    const batches: string[][] = [];
    for (let i = 0; i < texts.length; i += EMBEDDING_BATCH_SIZE) {
      batches.push(texts.slice(i, i + EMBEDDING_BATCH_SIZE));
    }

    log.info('Starting embedding generation', {
      totalChunks: chunks.length,
      totalChars,
      batchCount: batches.length,
      batchSize: EMBEDDING_BATCH_SIZE,
      requiresBatching: batches.length > 1
    });

    // Process batches sequentially with retry logic
    const allEmbeddings: number[][] = [];

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      let lastError: Error | null = null;
      let batchEmbeddings: number[][] | null = null;

      // Retry logic for each batch
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          log.info(`Processing embedding batch ${batchIndex + 1}/${batches.length} (attempt ${attempt}/${MAX_RETRIES})`, {
            batchSize: batch.length,
            charsInBatch: batch.reduce((sum, t) => sum + t.length, 0)
          });

          const { data, error } = await supabase.functions.invoke('ai-embeddings', {
            body: { texts: batch, inputType: 'search_document' },
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          });

          if (error) {
            lastError = new Error(
              `Embedding generation failed on batch ${batchIndex + 1}/${batches.length}: ${error.message || JSON.stringify(error)}`
            );
            log.warn(`Batch ${batchIndex + 1} attempt ${attempt} failed`, { error: JSON.stringify(error) });

            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1);
              log.info(`Retrying batch ${batchIndex + 1} in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
            throw lastError;
          }

          if (!data) {
            lastError = new Error(`No data returned from embedding service for batch ${batchIndex + 1}`);
            log.warn(`Batch ${batchIndex + 1} attempt ${attempt} returned no data`);

            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1);
              log.info(`Retrying batch ${batchIndex + 1} in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
            throw lastError;
          }

          // Check for error in response data
          if (data.error) {
            lastError = new Error(`Embedding service error in batch ${batchIndex + 1}: ${data.error}`);
            log.warn(`Batch ${batchIndex + 1} attempt ${attempt} returned error`, {
              error: data.error,
              details: data.details
            });

            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1);
              log.info(`Retrying batch ${batchIndex + 1} in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
            throw lastError;
          }

          if (!data.embeddings || data.embeddings.length !== batch.length) {
            lastError = new Error(`Embedding count mismatch in batch ${batchIndex + 1}`);
            log.warn(`Batch ${batchIndex + 1} attempt ${attempt} embedding count mismatch`, {
              expected: batch.length,
              received: data.embeddings?.length || 0
            });

            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1);
              log.info(`Retrying batch ${batchIndex + 1} in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
            throw lastError;
          }

          // Success!
          batchEmbeddings = data.embeddings;
          log.info(`Batch ${batchIndex + 1}/${batches.length} completed successfully on attempt ${attempt}`, {
            embeddingsGenerated: data.embeddings.length,
            dimensions: data.dimensions
          });
          break; // Exit retry loop on success

        } catch (error) {
          lastError = error as Error;
          log.warn(`Batch ${batchIndex + 1} attempt ${attempt} failed with error`, { error });

          if (attempt < MAX_RETRIES) {
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1);
            log.info(`Retrying batch ${batchIndex + 1} in ${delay}ms after error...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            throw lastError;
          }
        }
      }

      if (!batchEmbeddings) {
        throw lastError || new Error(`Failed to generate embeddings for batch ${batchIndex + 1} after ${MAX_RETRIES} attempts`);
      }

      allEmbeddings.push(...batchEmbeddings);
    }

    log.info('All embeddings generated successfully', {
      totalEmbeddings: allEmbeddings.length,
      batches: batches.length,
      dimensions: allEmbeddings[0]?.length || 0
    });

    // Attach embeddings to chunks
    return chunks.map((chunk, i) => ({
      ...chunk,
      embedding: allEmbeddings[i]
    }));
  } catch (error) {
    log.error('Embedding generation failed after all retries', { chunkCount: chunks.length }, error as Error);
    throw error;
  }
}

// ============================================================================
// Document Upload
// ============================================================================

interface UploadDocumentParams {
  file: File;
  title: string;
  documentType: DocumentType;
  category: string;
  keywords: string[];
  complianceTags?: string[];
  expiresAt?: string;
  onProgress?: (progress: number) => void;
}

/**
 * Get organization ID for current user
 */
async function getCurrentUserOrganizationId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  // Try to get from user_metadata first
  if (user.user_metadata?.organization_id) {
    return user.user_metadata.organization_id;
  }

  // Try app_metadata
  if (user.app_metadata?.organization_id) {
    return user.app_metadata.organization_id;
  }

  // Fallback: query users table
  const { data: userData, error } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (error || !userData?.organization_id) {
    throw new Error('User organization not found');
  }

  return userData.organization_id;
}

/**
 * Complete document upload pipeline:
 * 1. Upload file to storage
 * 2. Extract text
 * 3. Chunk text
 * 4. Generate embeddings
 * 5. Store in database
 */
export async function uploadDocument(params: UploadDocumentParams): Promise<AIDocument> {
  const { file, title, documentType, category, keywords, complianceTags, expiresAt, onProgress } = params;

  log.info('Starting document upload', {
    fileName: file.name,
    fileSize: file.size,
    documentType
  });

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
  }

  try {
    // Get organization ID
    const organizationId = await getCurrentUserOrganizationId();

    // Step 1: Upload to Supabase Storage (10%)
    onProgress?.(10);
    const fileName = `${Date.now()}-${file.name}`;
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, file);

    if (uploadError) {
      throw new Error(`Storage upload failed: ${uploadError.message}`);
    }

    log.info('File uploaded to storage', { fileName: uploadData.path });

    // Step 2: Extract text (30%)
    onProgress?.(30);
    const content = await extractText(file);

    if (content.length === 0) {
      throw new Error('Document contains no text content');
    }

    log.info('Text extracted', { contentLength: content.length });

    // Step 3: Chunk text (50%)
    onProgress?.(50);
    let chunks = chunkText(content);

    // Step 4: Generate embeddings (70%)
    onProgress?.(70);
    chunks = await generateEmbeddings(chunks);

    // Step 5: Insert into database (90%)
    onProgress?.(90);
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Insert document metadata (without chunks)
    const documentData = {
      organization_id: organizationId,
      title,
      content,
      document_type: documentType,
      category,
      keywords,
      compliance_tags: complianceTags || [],
      source_file: fileName,
      file_size: file.size,
      uploaded_by: user.email || user.id,
      expires_at: expiresAt || null,
      status: 'ready',
      version: 1,
      chunk_count: chunks.length,
      total_tokens: chunks.reduce((sum, c) => sum + c.tokenCount, 0)
    };

    const { data: insertedDoc, error: insertError } = await supabase
      .from('ai_documents')
      .insert(documentData)
      .select()
      .single();

    if (insertError) {
      throw new Error(`Database insert failed: ${insertError.message}`);
    }

    // Insert chunks into separate table with page metadata
    const chunkData = chunks.map(chunk => ({
      document_id: insertedDoc.id,
      chunk_index: chunk.index,
      content: chunk.content,
      token_count: chunk.tokenCount,
      embedding: chunk.embedding,
      metadata: chunk.metadata || null // Page info for citation linking
    }));

    const { error: chunksError } = await supabase
      .from('ai_document_chunks')
      .insert(chunkData);

    if (chunksError) {
      // Rollback: delete the document we just created
      await supabase.from('ai_documents').delete().eq('id', insertedDoc.id);
      throw new Error(`Failed to insert chunks: ${chunksError.message}`);
    }

    onProgress?.(100);

    log.info('Document uploaded successfully', {
      documentId: insertedDoc.id,
      chunkCount: chunks.length
    });

    // Convert database format to AIDocument type
    return {
      id: insertedDoc.id,
      organizationId: insertedDoc.organization_id,
      title: insertedDoc.title,
      content: insertedDoc.content,
      chunks: chunks.map(c => ({
        index: c.index,
        content: c.content,
        tokenCount: c.tokenCount,
        embedding: c.embedding
      })),
      documentType: insertedDoc.document_type,
      category: insertedDoc.category,
      keywords: insertedDoc.keywords,
      complianceTags: insertedDoc.compliance_tags,
      sourceFile: insertedDoc.source_file,
      fileSize: insertedDoc.file_size,
      uploadedAt: insertedDoc.created_at,
      uploadedBy: insertedDoc.uploaded_by,
      expiresAt: insertedDoc.expires_at,
      status: insertedDoc.status,
      version: insertedDoc.version,
      citationCount: insertedDoc.citation_count || 0,
      lastCitedAt: insertedDoc.last_cited_at,
      relevanceScore: insertedDoc.avg_relevance_score
    };
  } catch (error) {
    log.error('Document upload failed', { fileName: file.name }, error as Error);
    throw error;
  }
}

// ============================================================================
// Document Queries
// ============================================================================

/**
 * Get all documents for current organization
 */
export async function getDocuments(): Promise<AIDocument[]> {
  const organizationId = await getCurrentUserOrganizationId();

  // Get documents with investor join for grouping
  const { data: documents, error: docsError } = await supabase
    .from('ai_documents')
    .select(`
      *,
      investors(investor_name)
    `)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (docsError) {
    throw new Error(`Failed to fetch documents: ${docsError.message}`);
  }

  if (!documents || documents.length === 0) {
    return [];
  }

  // Get all chunks for these documents (exclude embedding for performance)
  const documentIds = documents.map(d => d.id);
  const { data: chunks, error: chunksError } = await supabase
    .from('ai_document_chunks')
    .select('id, document_id, chunk_index, content, token_count, metadata')
    .in('document_id', documentIds)
    .order('document_id')
    .order('chunk_index');

  if (chunksError) {
    throw new Error(`Failed to fetch chunks: ${chunksError.message}`);
  }

  // Group chunks by document_id
  interface ChunkRow {
    id: string;
    document_id: string;
    chunk_index: number;
    content: string;
    token_count: number;
    metadata?: Record<string, unknown>;
  }

  const chunksByDocId = new Map<string, ChunkRow[]>();
  (chunks || []).forEach(chunk => {
    if (!chunksByDocId.has(chunk.document_id)) {
      chunksByDocId.set(chunk.document_id, []);
    }
    chunksByDocId.get(chunk.document_id)!.push(chunk);
  });

  // Combine documents with their chunks
  return documents.map(doc => ({
    id: doc.id,
    organizationId: doc.organization_id,
    title: doc.title,
    content: doc.content,
    chunks: (chunksByDocId.get(doc.id) || []).map((c) => ({
      index: c.chunk_index,
      content: c.content,
      tokenCount: c.token_count
      // Note: embedding excluded for performance
    })),
    documentType: doc.document_type,
    category: doc.category,
    keywords: doc.keywords,
    complianceTags: doc.compliance_tags,
    sourceFile: doc.source_file,
    fileSize: doc.file_size,
    uploadedAt: doc.created_at,
    uploadedBy: doc.uploaded_by,
    expiresAt: doc.expires_at,
    status: doc.status,
    version: doc.version,
    citationCount: doc.citation_count || 0,
    lastCitedAt: doc.last_cited_at,
    relevanceScore: doc.avg_relevance_score,
    totalTokens: doc.total_tokens || 0,
    investorId: doc.investor_id,
    investorName: doc.investors?.investor_name
  }));
}

/**
 * Delete a document
 */
export async function deleteDocument(documentId: string): Promise<void> {
  const { error } = await supabase
    .from('ai_documents')
    .delete()
    .eq('id', documentId);

  if (error) {
    throw new Error(`Failed to delete document: ${error.message}`);
  }

  log.info('Document deleted', { documentId });
}

/**
 * Update document metadata
 */
export async function updateDocument(
  documentId: string,
  updates: Partial<Pick<AIDocument, 'title' | 'category' | 'keywords' | 'complianceTags' | 'expiresAt'>>
): Promise<void> {
  const dbUpdates: Record<string, string | string[] | null | undefined> = {};

  if (updates.title) {
dbUpdates.title = updates.title;
}
  if (updates.category) {
dbUpdates.category = updates.category;
}
  if (updates.keywords) {
dbUpdates.keywords = updates.keywords;
}
  if (updates.complianceTags) {
dbUpdates.compliance_tags = updates.complianceTags;
}
  if (updates.expiresAt !== undefined) {
dbUpdates.expires_at = updates.expiresAt;
}

  const { error } = await supabase
    .from('ai_documents')
    .update(dbUpdates)
    .eq('id', documentId);

  if (error) {
    throw new Error(`Failed to update document: ${error.message}`);
  }

  log.info('Document updated', { documentId, updates });
}

// ============================================================================
// Queue-Based Upload (Async Processing via GPU Worker)
// ============================================================================

interface UploadDocumentAsyncParams {
  file: File;
  title: string;
  documentType: DocumentType;
  category: string;
  keywords: string[];
  complianceTags?: string[];
  expiresAt?: string;
}

/**
 * Upload document and queue for async processing via GPU worker.
 * Returns immediately with document ID - processing happens in background.
 *
 * Use this for:
 * - Large documents that would timeout in browser
 * - Batch uploads where immediate completion isn't required
 * - When GPU acceleration is preferred over browser-based extraction
 *
 * The document will have status='processing' until the worker completes,
 * at which point it becomes status='active'.
 */
export async function uploadDocumentAsync(params: UploadDocumentAsyncParams): Promise<{ documentId: string; queueId: string }> {
  const { file, title, documentType, category, keywords, complianceTags, expiresAt } = params;

  log.info('Starting async document upload', {
    fileName: file.name,
    fileSize: file.size,
    documentType
  });

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
  }

  try {
    // Get organization ID
    const organizationId = await getCurrentUserOrganizationId();

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Step 1: Upload file to storage
    const fileName = `${Date.now()}-${file.name}`;
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, file);

    if (uploadError) {
      throw new Error(`Storage upload failed: ${uploadError.message}`);
    }

    log.info('File uploaded to storage', { fileName: uploadData.path });

    // Step 2: Create document record with processing status
    const documentData = {
      organization_id: organizationId,
      title,
      content: null, // Will be populated by worker
      document_type: documentType,
      category,
      keywords,
      compliance_tags: complianceTags || [],
      source_file: fileName,
      file_size: file.size,
      uploaded_by: user.email || user.id,
      expires_at: expiresAt || null,
      status: 'processing', // Worker will update to 'active' when done
      version: 1,
      chunk_count: 0,
      total_tokens: 0
    };

    const { data: insertedDoc, error: insertError } = await supabase
      .from('ai_documents')
      .insert(documentData)
      .select('id')
      .single();

    if (insertError) {
      throw new Error(`Database insert failed: ${insertError.message}`);
    }

    log.info('Document record created', { documentId: insertedDoc.id });

    // Step 3: Queue for GPU worker processing
    const { queueId } = await queueAIDocumentForProcessing(insertedDoc.id);

    log.info('Document queued for async processing', {
      documentId: insertedDoc.id,
      queueId,
      fileName
    });

    return {
      documentId: insertedDoc.id,
      queueId
    };

  } catch (error) {
    log.error('Async document upload failed', { fileName: file.name }, error as Error);
    throw error;
  }
}
